import { AppLogo } from '@/assets/logos/app-logo';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

export type DiscordConnectPanelStatus = 'loading' | 'success' | 'error';

type DiscordConnectPanelProps = {
  status: DiscordConnectPanelStatus;
  title: string;
  message: string;
  helperText?: string;
};

function statusIcon(status: DiscordConnectPanelStatus) {
  if (status === 'loading') {
    return <Loader2 className="size-5 animate-spin" />;
  }
  if (status === 'success') {
    return <CheckCircle2 className="size-5" />;
  }
  return <XCircle className="size-5" />;
}

function statusTone(status: DiscordConnectPanelStatus) {
  if (status === 'loading') {
    return 'border-zinc-200 bg-zinc-100 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300';
  }
  if (status === 'success') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300';
  }
  return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300';
}

export function DiscordConnectPanel({
  status,
  title,
  message,
  helperText,
}: DiscordConnectPanelProps) {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[#f7f7f4] px-6 py-10 text-[#171717] dark:bg-[#11110f] dark:text-white">
      <div className="w-full max-w-[30rem] rounded-lg border border-black/10 bg-white p-6 shadow-[0_14px_40px_rgba(0,0,0,0.10)] dark:border-white/10 dark:bg-[#191917]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-[#171717] text-white dark:bg-white dark:text-[#171717]">
              <AppLogo className="size-6" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-5">Bridge</div>
              <div className="truncate text-xs leading-5 text-zinc-500 dark:text-zinc-400">
                Discord direct messages
              </div>
            </div>
          </div>
          <div className="shrink-0 rounded-full border border-[#5865f2]/20 bg-[#5865f2]/10 px-3 py-1 text-xs font-medium text-[#4752c4] dark:text-[#a8b0ff]">
            Discord
          </div>
        </div>

        <div className="mt-8 flex gap-4">
          <div
            className={`flex size-10 shrink-0 items-center justify-center rounded-lg border ${statusTone(
              status
            )}`}
          >
            {statusIcon(status)}
          </div>
          <div className="min-w-0 pt-1">
            <h1 className="text-2xl font-semibold leading-tight tracking-normal">
              {title}
            </h1>
            <p className="mt-3 text-sm leading-6 text-zinc-600 dark:text-zinc-300">
              {message}
            </p>
            {helperText ? (
              <p className="mt-5 text-xs leading-5 text-zinc-500 dark:text-zinc-400">
                {helperText}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
